import { useCallback, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'

import Clickable from 'components/Common/Clickable'
import Image from 'components/Common/Image'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import { getCruiseItineraryLocationLabel } from 'lib/cruises/cruiseOfferUtils'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const PLACEHOLDER_IMAGE_ID = 'szx0yiy4fy4pm81q9te'

const MobileScrollOuter = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
`

const MobileCardsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: ${rem(12)};
  width: max-content;
  padding-bottom: ${rem(4)};
`

const MobileCardWrapper = styled.div`
  flex: 0 0 ${rem(200)};
  width: ${rem(200)};
`

const MobileDayCard = styled(Clickable)`
  position: relative;
  width: 100%;
  min-height: ${rem(100)};
  overflow: hidden;
  border-radius: ${props => props.theme.borderRadius.S};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &.selected {
    transform: scale(1.02);
    z-index: 1;
    box-shadow: ${props => props.theme.shadow.flat.medium};
  }

  ${mediaHoverable} {
    &:hover {
      box-shadow: ${props => props.theme.shadow.flat.small};
    }
  }
`

const MobileCardImage = styled(Image)`
  position: absolute;
  inset: 0;
  border-radius: ${props => props.theme.borderRadius.S};
  outline: 2px solid transparent;
  opacity: 0.6;
  transition: opacity 0.2s ease, outline-color 0.15s ease;

  &.selected {
    opacity: 1;
    outline-color: ${props => props.theme.palette.brand.primary.normal};
  }

  ${mediaHoverable} {
    ${MobileDayCard}:hover &:not(.selected) {
      opacity: 1;
    }

    ${MobileDayCard}:hover & {
      outline-color: ${props => props.theme.palette.brand.primary.normal};
    }
  }
`

const MobileCardGradient = styled.div`
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(to top, rgb(1 11 26 / 45%) 0%, rgb(1 7 21 / 30%) 70%, #09253300 100%);
  pointer-events: none;
  height: 70%;
  top: 30%;
`

const MobileCardBottom = styled.div`
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  padding: ${rem(12)} ${rem(12)} ${rem(10)};
  pointer-events: none;
`

export interface CruiseItineraryMobileSidebarProps {
  visibleItems: Array<{ item: App.CruiseItineraryItem; index: number }>
  selectedIndex: number
  onSelectItem: (index: number) => void
  itineraryByDay?: Map<number, App.Cruises.OfferItineraryV2Item>
  offerFallbackImageId?: string
}

function CruiseItineraryMobileSidebar({
  visibleItems,
  selectedIndex,
  onSelectItem,
  itineraryByDay,
  offerFallbackImageId,
}: CruiseItineraryMobileSidebarProps) {
  const intl = useIntl()
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map())

  useEffect(() => {
    const card = cardRefs.current.get(selectedIndex)
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' })
  }, [selectedIndex, visibleItems])

  const setCardRef = useCallback((index: number, el: HTMLDivElement | null) => {
    const map = cardRefs.current
    if (el) {
      map.set(index, el)
    } else {
      map.delete(index)
    }
  }, [])

  if (visibleItems.length === 0) {
    return null
  }

  return (
    <MobileScrollOuter>
      <MobileCardsRow>
        {visibleItems.map(({ item, index }) => {
          const itineraryItem = itineraryByDay?.get(item.startDay)
          const imageId =
            itineraryItem?.attractions[0]?.imageId ??
            itineraryItem?.imageId ??
            offerFallbackImageId ??
            PLACEHOLDER_IMAGE_ID
          const locationLabel =
            getCruiseItineraryLocationLabel(item, intl.formatMessage({
              id: 'eglatGtb5bLey9o-rWTz',
              defaultMessage: 'At Sea',
              description: 'Itinerary label for days spent at sea during a cruise',
            }))
          const isSelected = index === selectedIndex

          return (
            <MobileCardWrapper
              key={`mobile-sidebar-${item.startDay}-${item.title}-${index}`}
              ref={el => {
                setCardRef(index, el)
              }}
            >
              <MobileDayCard
                onClick={() => {
                  onSelectItem(index)
                }}
                className={clsx({ selected: isSelected })}
              >
                <MobileCardImage
                  id={imageId}
                  alt={locationLabel}
                  fit="center"
                  objectFit="cover"
                  width={400}
                  height={220}
                  className={clsx({ selected: isSelected })}
                />
                <MobileCardGradient />
                <MobileCardBottom>
                  <Group direction="vertical" gap={4}>
                    <BodyText variant="large" colour="neutral-eight" weight="bold">
                      <FormattedMessage
                        id="7UZX7VsN58ZjEXsFisyN"
                        defaultMessage="Day {day}"
                        description="Day number label in trip itinerary item location summary"
                        values={{ day: item.startDay }}
                      />
                    </BodyText>
                    <Caption variant="large" colour="neutral-eight">
                      {locationLabel}
                    </Caption>
                  </Group>
                </MobileCardBottom>
              </MobileDayCard>
            </MobileCardWrapper>
          )
        })}
      </MobileCardsRow>
    </MobileScrollOuter>
  )
}

export default CruiseItineraryMobileSidebar
